import RowStack from '@/common/components/common/row-stack/RowStack';
import BTButton from '@common-components/bt-button/bt-button';
import BTThemeProvider from '@common-components/bt-theme-provider';
import { DBSessionThunks } from '@common-reducers/DBServiceThunks';
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { toggleHowToScanQrModal, toggleSocketModal } from '@common-reducers/MainReducer';
import { activeSubscriptionSelector, selectCurrentUserSession } from "@common-reducers/UserSelectors";
import { createNewSessionThunk } from '@common-reducers/UserThunks';
import { AnalyticsService } from "@common-services/analytics-service";
import { getRuntimeUrl } from "@common-services/storage";
import { openPaymentSite } from '@common-services/utils';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Button, CircularProgress, Dialog, DialogContent, LinearProgress, Slide, Stack, Typography } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { Box } from '@mui/system';
import { namespace } from '@common-config/constants';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TbArrowBigUp } from 'react-icons/tb';
import QRCode from "react-qr-code";
import { CloseIcon } from "./qr-icons";
import classes from "./qr.module.scss";

const analyticsService = new AnalyticsService();

const closeLaptopVideo = getRuntimeUrl("img/close_laptop.mp4");
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const QrModal = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(namespace);
  const accessToken = useAppSelector((state) => state.UserReducer.accessToken);
  let session = useAppSelector(state => selectCurrentUserSession(state));
  const isSocketModalOpen = useAppSelector((state) => state.MainReducer.isSocketModalOpen);
  const activeSubscription = useAppSelector(activeSubscriptionSelector);
  const allowToScanSocket = activeSubscription?.hasSocket();
  const [showDefaultCase, setShowDefaultCase] = useState(false);

  // Add a ref to store the polling interval
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const defaultCaseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const selectCurrentSessionProgress = (status: string) => {
    const statusProgressMap: Record<string, number> = {
      'disconnected': 0,
      'initializing': 20,
      'browser_started': 40,
      'navigating': 60,
      'waiting_for_whatsapp_page': 60,
      'OPENING': 70,
      'unpaired': 80,
      'syncing': 90,
      'PAIRING': 90,
      'qrcode': 100,
      'connected': 100
    };

    return statusProgressMap[status] || 0
  }

  // Add helper function to get status message
  const getProgressMessage = (status: string) => {
    switch (status) {
      case 'initializing': return t('qr.progress.initializing', 'Getting things ready...');
      case 'browser_started': return t('qr.progress.browserStarted', 'Setting up environment...');
      case 'navigating': return t('qr.progress.almostThere', 'Almost there...');
      case 'waiting_for_whatsapp_page': return t('qr.progress.almostThere', 'Almost there...');
      case 'unpaired': return t('qr.progress.preparingQr', 'Preparing QR code...');
      case 'qrcode': return t('qr.progress.qrReady', 'QR code ready!');
      case 'connected': return t('qr.progress.success', 'Success!');
      case 'PAIRING': return t('qr.progress.syncing', 'Syncing...');
      case 'OPENING': return t('qr.progress.syncing', 'Syncing...');
      case 'syncing': return t('qr.progress.syncing', 'Syncing...');
      default: return t('qr.progress.startingUp', 'Starting up...');
    }
  };

  console.log(`QrModal: sessionStatus: ${session?.status}, sessionId: ${session?._id}`)

  const createNewSession = useCallback(async () => {
    console.log(`Requesting session`);
    dispatch(createNewSessionThunk())
  }, [dispatch])

  const [prevStatus, setPrevStatus] = useState(session?.status);



  // Start polling when modal opens
  useEffect(() => {

    if (isSocketModalOpen && allowToScanSocket) {
      // Initial fetch

      // Set up polling interval (every 6 seconds)
      pollingIntervalRef.current = setInterval(() => {
        dispatch(DBSessionThunks.find({ cleanStateIfResultIsEmpty: true }));
      }, 6000);

      // Clean up interval when component unmounts or modal closes
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [isSocketModalOpen, allowToScanSocket]);

  useEffect(() => {
    if (isSocketModalOpen && allowToScanSocket) {
      createNewSession();
      analyticsService.event("qr_code_requested", {
        category: "connection",
        action: "request_qr_code"
      });
    }
  }, [allowToScanSocket, createNewSession, isSocketModalOpen]);

  useEffect(() => {
    if (session?.status !== prevStatus) {
      analyticsService.event("connection_status_changed", {
        category: "connection",
        action: "status_change",
        status: session?.status
      });

      if (session?.status === 'connected') {
        analyticsService.event('whatsapp_connected', {
          category: 'onboarding',
          connection_method: 'gateway',
        });
      }

      setPrevStatus(session?.status);
    }
  }, [prevStatus, session?.status]);

  // Reset the showDefaultCase state when session status changes
  useEffect(() => {
    if (!session?.status) {
      defaultCaseTimeoutRef.current = setTimeout(() => {
        setShowDefaultCase(true);
      }, 6000);
    }

  }, [session?.status]);

  //sessionStatus = 'connecting'

  const qrContainerContent = (size?: number) => {
    switch (session?.status) {
      case 'initializing':
      case 'browser_started':
      case 'navigating':
      case 'waiting_for_whatsapp_page':
      case 'unpaired':
      case 'syncing':
        return <Stack spacing={2} alignItems="center">
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={selectCurrentSessionProgress(session?.status)}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'darkgray',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor: 'gray'
                }
              }}
            />
            <Typography
              sx={{
                mt: 1,
                textAlign: 'center',
                color: 'var(--color-primary)',
                fontSize: '0.875rem'
              }}
            >
              {getProgressMessage(session?.status)}
            </Typography>
          </Box>
          <Typography sx={{ color: 'var(--color-primary)' }}>
            {t('qr.pleaseBePatient', 'Please be patient...')}<br />{t('qr.gettingQrTakesTime', 'Getting QR can take 1-2 minutes')}
          </Typography>
        </Stack>
      case 'connected':
        return <span>{t('qr.connected', 'Connected!')}</span>;
      case 'qrcode':
        if (session?.qrcode?.indexOf('data:image') === -1) {
          return <QRCode value={session?.qrcode} size={size} />;
        } else {
          return <img src={session?.qrcode} alt={t('qr.qrCodeAlt', 'QR Code')} style={{ width: size, height: size }} />;
        }
      case 'OPENING':
      case 'PAIRING':
      case 'connecting':
        return <span className={classes.loading}>{t('qr.connecting', 'Connecting...')}</span>;
      case 'scan-qr-again':
        return <div>
          <Typography
            sx={{ fontWeight: "bold", fontSize: "22px", lineHeight: 1.5, color: 'var(--color-primary)' }}>
            <Trans
              t={t}
              i18nKey="qr.scanFailed"
              components={{ u: <u />, generating: <span className={classes.loadingClean} /> }}
            />
          </Typography>
        </div>;
      case 'disconnected':
        return <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography sx={{ color: 'var(--color-primary)' }}>
            {t('qr.pleaseBePatient', 'Please be patient...')}<br />{t('qr.gettingQrTakesTime', 'Getting QR can take 1-2 minutes')}
          </Typography>
        </Stack>;
      case null:
      default:

        return showDefaultCase ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              textAlign: "center",
              margin: "10px 0",
              border: "1px solid #dfdfdf",
              borderRadius: "10px",
              padding: "15px"
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: 'var(--color-primary)',
                opacity: 0.8,
                marginBottom: 2
              }}
            >
              <Trans t={t} i18nKey="qr.generateIssue" />
            </Typography>
            <BTButton
              onClick={createNewSession}
              startIcon={<RestartAltIcon />}
              btvariant="primary"
              sx={{
                padding: "10px 24px",
                fontSize: "1rem"
              }}
            >
              {t('qr.regenerate', 'Regenerate QR Code')}
            </BTButton>
          </Stack>
        ) : null
    }
  }

  const disconnectedState = () => <Stack justifyContent={"center"} alignItems={"center"} style={{ width: "600px" }} >
    <div className={classes.close_button_container}>
      <span onClick={() => { dispatch(toggleSocketModal(false)) }}>
        {CloseIcon}
      </span>
    </div>
    <div className={classes.content} style={{ maxWidth: "500px" }}>

      <Box sx={{ mb: !allowToScanSocket ? 3 : 1 }}>
        <Typography
          sx={{ fontWeight: "bold", fontSize: "22px", lineHeight: 1.2, color: 'var(--color-primary)' }}>
          {t('qr.offerTitle', "Don't want to leave your computer open?")}
        </Typography>
        <Typography sx={{ marginTop: "10px", color: 'var(--color-primary)' }}>
          {t('qr.offerBody', 'Scan this QR code with your phone WhatsApp, and stop worrying about leaving your computer open')}
        </Typography>
      </Box>

      <Stack alignItems={"center"}>
        {allowToScanSocket && (
          <div>
            <div className={classes.qrContainer} style={{ margin: "10px 0" }}>
              {qrContainerContent(264)}
            </div>
            <p style={{ marginBottom: "8px" }}
              onClick={() => { dispatch(toggleHowToScanQrModal(true)) }}
              className={classes.howto_scan}>{t('qr.howToScan', 'How to scan this QR code?')}
            </p>
          </div>
        )}
        {
          !allowToScanSocket &&
          <Stack justifyContent={"center"} alignItems={"center"}
            sx={{ maxWidth: '400px', padding: "10px 7px 15px 7px", mb: "28px", backgroundColor: "#ededed", borderRadius: "16px" }}>

            <BTButton onClick={() => openPaymentSite(accessToken ?? '')}
              startIcon={<TbArrowBigUp size={"16"} />}
              sx={{ fontSize: "16px", padding: "6px 20px", fontWeight: 500, color: 'var(--tasks-board-bg)', }} btvariant="pink"  >
              {t('qr.upgrade', 'UPGRADE')}
            </BTButton>

            <Typography sx={{ fontSize: "16px", marginTop: "6px", color: 'var(--color-primary)' }}>
              <Trans
                t={t}
                i18nKey="qr.upgradeBody"
                /* Was "Upgrade to <b>Campaigner</b> plan". There is no Campaigner plan —
                   the plans are Basic / Standard / Pro, and Campaigner is a legacy name
                   `plan-selection` maps ONTO Pro. Offline scanning is specifically the
                   Pro discriminator: this dialog is gated on `hasSocket()`, which for a
                   Chargebee subscription is the `offline-socket` entitlement or plan tier
                   Pro (bt-common subscription-object.class.ts). So the plan it sells is Pro.
                   `defaults` added at the same time: this key exists only in en and he-IL,
                   so the other seven locales were rendering the literal string
                   "qr.upgradeBody" at the moment we ask someone to pay. */
                defaults="Upgrade to <b>Pro</b> to scan the QR code and keep your messages sending while your computer is off."
                components={{ b: <b /> }}
              />
            </Typography>

            <Typography sx={{ fontSize: "16px", marginTop: "6px", color: 'var(--color-primary)' }}>
              {t('qr.upgradeRefreshNote', "* Once you've made the purchase, don't forget to refresh the page to activate the feature.")}
            </Typography>
          </Stack>
        }

      </Stack>
      <RowStack justifyContent={"center"} alignItems={"center"}>
        <video autoPlay loop playsInline muted src={closeLaptopVideo}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: "15px",
            maxWidth: '302px',
          }} />
      </RowStack>
      <button style={{ color: 'var(--color-primary)' }} onClick={() => { dispatch(toggleSocketModal(false)) }} className={classes.dismiss_button} >
        {t('qr.dismiss', "No thanks, I'll leave my computer open")}
      </button>
    </div>
  </Stack>

  const connectedState = () => <Stack justifyContent={"center"} alignItems={"center"} style={{ width: "600px" }} >
    <div className={classes.close_button_container}>
      <span onClick={() => { dispatch(toggleSocketModal(false)) }}>
        {CloseIcon}
      </span>
    </div>
    <div className={classes.content} >
      <Box sx={{ mb: "10px" }}>
        <Typography
          sx={{ fontWeight: "bold", fontSize: "36px", lineHeight: 1.2, color: '#49B26B' }}>
          {t('qr.connectedTitle', 'You are connected!🥳')}
        </Typography>
        <Typography sx={{ marginTop: "10px", color: 'var(--color-primary)' }}>
          <Trans t={t} i18nKey="qr.connectedBody" components={{ b: <b /> }} />
        </Typography>
      </Box>

      <RowStack justifyContent={"center"} alignItems={"center"}>
        <video autoPlay loop playsInline muted src={closeLaptopVideo}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: "15px",
            maxWidth: '302px',
          }} />
      </RowStack>

      <Stack justifyContent={"center"} alignItems={"center"} style={{ marginTop: "10px" }}>
        <Button onClick={() => { dispatch(toggleSocketModal(false)) }}
          variant='outlined'
          color="success"
          style={{
            padding: "10px 20px",
          }}>
          {t('qr.gotIt', 'Got it!')}
        </Button>
      </Stack>
      <Typography sx={{ marginTop: "12px", color: 'var(--color-primary)', fontSize: "14px" }}>
        <Trans
          t={t}
          i18nKey="qr.disconnectNote"
          components={{
            tutorial: <a href="https://faq.whatsapp.com/834124628020911/?cms_platform=web" target="_blank" rel="noopener noreferrer" />,
          }}
        />
      </Typography>
    </div>
  </Stack>

  return (
    <BTThemeProvider>
      <Dialog
        TransitionComponent={Transition}
        maxWidth='lg'
        open={isSocketModalOpen}
        onClose={() => { dispatch(toggleSocketModal(false)) }}
        PaperProps={{
          sx: {
            backgroundColor: 'var(--tasks-board-bg)',
            color: 'var(--color-text)'
          }
        }}
      >
        <DialogContent>
          {session?.status === 'connected' ? connectedState() : disconnectedState()}
        </DialogContent>
      </Dialog >
    </BTThemeProvider>
  );
}
export default QrModal;
